import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import Text from 'core/elements/Text'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { isAdminRole } from 'k8s/util/helpers'
import { pick, prop } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import CreateNetworkModal from './CreateNetworkModal'
import CreatePhysicalNetworkModal from './CreatePhysicalNetworkModal'
import EditPhysicalNetworkModal from './EditPhysicalNetworkModal'
import ShareNetworkWithSpecificTenants from './ShareNetworkWithSpecificTenants'
import { deleteNetwork, listNetworks } from './actions'
import { INetworkDetailsPageTabs } from './network-details/model'
import { networksSelector, physicalNetworksSelector } from './selectors'
import DeleteDialog from 'core/components/DeleteDialog'
import { formatTimestampToLocal } from '../vms/helpers'

type ModelDataKey = DataKeys.OpenstackNetworks
type SelectorModel = ArrayElement<ReturnType<typeof networksSelector>>
type ActionParams = InferActionParams<typeof listNetworks>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackNetworks',
  listTablePrefs,
)

const searchTargets = [
  'name',
  'id',
  'subnetDetails',
  'provider:network_type',
  'status',
  'availableIps',
  'provider:physical_network',
  'provider:segmentation_id',
]

const searchFunctions = [
  {
    property: 'subnetDetails',
    searchFn: (value, searchString) => {
      return value.some(
        (subnet) =>
          subnet.name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          subnet.cidr.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
      )
    },
  },
]

const SubnetsCellComponent = ({ value }) => {
  return (
    <>
      {value.map((subnet) => (
        <Text variant="body2" key={subnet.id}>
          {subnet.name}: <b>{subnet.cidr}</b>
        </Text>
      ))}
    </>
  )
}

export const SharedWithTenants = ({ value, item }) => {
  return (
    <>
      {value
        ? 'All Tenants'
        : item?.sharedTenants?.length > 0
        ? item?.sharedTenants?.map((i) => i.tenantName).join(', ')
        : 'None'}
    </>
  )
}

const networkColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id }) =>
        routeManager.routes.openstack.networkDetails.path({
          id,
          tab: INetworkDetailsPageTabs.Overview,
        }),
    }),
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'subnetDetails',
    label: 'Subnets',
    render: (value) => <SubnetsCellComponent value={value} />,
  },
  {
    key: 'port_security_enabled',
    label: 'Port Security',
    render: (val) => (val ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'provider:network_type',
    label: 'Network Type',
  },
  {
    key: 'mtu',
    label: 'MTU',
  },
  {
    key: 'provider:physical_network',
    label: 'Physical Network',
  },
  {
    key: 'router:external',
    label: 'External',
    render: (val) => (val ? 'Yes' : 'No'),
  },
  {
    key: 'provider:segmentation_id',
    label: 'Segmentation ID',
    render: (val) => (val ? val : 'N/A'),
  },
  {
    key: 'admin_state_up',
    label: 'Admin State',
    render: (val) => (val ? 'Up' : 'Down'),
  },
  {
    key: 'dns_domain',
    label: 'DNS Zone',
    render: (val) => (val ? val : '-'),
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'availableIps',
    label: 'Available IPs',
  },
  {
    key: 'shared',
    label: 'Shared Tenants',
    render: (value, item) => <SharedWithTenants item={item} value={value} />,
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

const ssuNetworkColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id }) =>
        routeManager.routes.openstack.networkDetails.path({
          id,
          tab: INetworkDetailsPageTabs.Overview,
        }),
    }),
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'subnetDetails',
    label: 'Subnets',
    render: (value) => <SubnetsCellComponent value={value} />,
  },
  {
    key: 'port_security_enabled',
    label: 'Port Security',
    render: (val) => (val ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'router:external',
    label: 'External',
    render: (val) => (val ? 'Yes' : 'No'),
  },
  {
    key: 'admin_state_up',
    label: 'Admin State',
    render: (val) => (val ? 'Up' : 'Down'),
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'shared',
    label: 'Shared Tenants',
    render: (value, item) => <SharedWithTenants item={item} value={value} />,
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

export default function NetworksListPage() {
  const [selectedNetwork, setSelectedNetwork] = useState<SelectorModel>(null)
  const [showShareNetworkModal, setShowShareNetworkModal] = useState<boolean>(false)
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { history } = useReactRouter()
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isAdminUser = isAdmin()
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  if (isVmware) {
    history.push(routeManager.routes.openstack.vmwareNetworks.path())
  }

  const { message, loading, reload } = useListAction(listNetworks, {
    params,
  })
  const data = useAppSelector(physicalNetworksSelector)

  const networksInTenant = useMemo(() => {
    return data.filter((network) => {
      return network.project_id === currentTenant || network.shared
    })
  }, [data, currentTenant])

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      label: 'Share Network With Specific Tenants',
      icon: 'edit',
      handleAction: ([network]) => {
        setSelectedNetwork(network)
        setShowShareNetworkModal(true)
      },
      refreshAfterSuccess: true,
      cond: (selectedRows) => selectedRows.length === 1 && !selectedRows[0]?.shared,
      disabledTooltip: (selectedRows) =>
        selectedRows.length > 1
          ? 'You can only share network with specific tenants for a single network at a time'
          : selectedRows[0]?.shared
          ? 'Network is already shared with all tenants'
          : '',
    },
  ]

  const DeletePhysicalNetworkDialog = useCallback(
    (props) => (
      <DeleteDialog
        {...props}
        deleteAction={deleteNetwork}
        title="Delete Network(s)"
        resourceLabel="Networks"
      />
    ),
    [],
  )

  return (
    <>
      <DocumentMeta title={isAdminRole(session) ? 'Physical Networks' : 'Networks'} />
      {isAdminUser ? (
        <>
          <CreatePhysicalNetworkModal addRoute={routeManager.routes.openstack.createNetwork} />
          {showShareNetworkModal && (
            <ShareNetworkWithSpecificTenants
              onClose={() => {
                setSelectedNetwork(null)
                setShowShareNetworkModal(false)
              }}
              network={selectedNetwork}
            />
          )}
        </>
      ) : (
        <CreateNetworkModal addRoute={routeManager.routes.openstack.createNetwork} />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.OpenstackNetworks}
        label={isAdminUser ? 'Physical Networks' : 'Networks'}
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={networksInTenant}
        columns={isAdminUser ? networkColumns : ssuNetworkColumns}
        addUrl={routeManager.routes.openstack.createNetwork.path()}
        addText={isAdminUser ? 'Create Physical Network' : 'Create Network'}
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteNetwork}
        DeleteDialogComponent={DeletePhysicalNetworkDialog}
        EditDialogComponent={EditPhysicalNetworkModal}
        batchActions={isAdminUser ? batchActions : []}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
